import React, { useEffect, useContext, useState, useMemo, useRef } from "react";
import { Grid, ListItem, Typography, useTheme, useMediaQuery, CircularProgress, Box } from "@mui/material";
import { Bar } from "../auth/Bar";
import { ProIconButton } from "../buttons/ProIconButton";
import { ProjectScheme } from "./projects_scheme/ProjectScheme";
import { useLoginState } from "../studioxr/auth/LoginState";
import { TeamsInterface, UserProfileRole } from "ts-interfaces";
import { BoardScheme } from "./boards_scheme/BoardScheme";
import { CollectionScheme } from "./collections_scheme/CollectionScheme";
import { RecentScheme } from "./recent_scheme/RecentScheme";
import { getDesignFirmsOfUser, getDesignersWithAccess } from "../v2/apis/teamsApi";
import { firebaseHelper } from "ui";
import { ProjectSchemeInterface } from "ts-interfaces";
import { useMattoState } from "../MattoState";
import { useParams, useNavigate } from 'react-router-dom';
import { useProjectState } from "../global-state/useProjectSchemas";
import { ProjectsSnackbarComponent } from "./components/ProjectsSnackbarComponent";
import { useEnsureProjects } from "../hooks/useEnsureProjects";

export const ProjectLayout = () => {
    const { tab } = useParams(); // Get tab parameter from URL
    const navigate = useNavigate();
    const profile = useLoginState((state) => state.profile);
    
    // Single State Object for Team Data to avoid re-renders
    const [teamData, setTeamData] = useState({
        designFirms: [],
        teamMembers: [] as TeamsInterface[],
        isLoading: false
    });

    const { currentUser, permissions, isProjectsLoading } = useEnsureProjects();

    // Comprehensive loading state check
    // Note: permissions can be null for free users - this is normal, not a loading state
    const isInitializing = useMemo(() => {
        // Check if core authentication and projects are still loading
        // Don't check permissions === null as free users legitimately have null permissions
        return !currentUser || !profile || isProjectsLoading;
    }, [currentUser, profile, isProjectsLoading]);

    useEffect(() => {
        const fetchDesignFirms = async () => {
            if (currentUser) {
                setTeamData(prev => ({ ...prev, isLoading: true }));
                
                try {
                    const designFirmsRes = await getDesignFirmsOfUser(currentUser.uid, firebaseHelper.db, true);
                    let teamMembers: TeamsInterface[] = [];
                    if (designFirmsRes?.length > 0) {
                        teamMembers = await getDesignersWithAccess(designFirmsRes[0], firebaseHelper.db, true);
                    }
                    
                    setTeamData({
                        designFirms: designFirmsRes,
                        teamMembers: teamMembers,
                        isLoading: false
                    });
                    
                } catch (error) {
                    console.error("Error fetching design firms:", error);
                    setTeamData(prev => ({ ...prev, isLoading: false }));
                }
            }
        };

        fetchDesignFirms();
    }, [currentUser]);

    // Function to handle tab changes and update URL
    const handleTabChange = (newTab) => {
        navigate(`/projects/${newTab.toLowerCase()}`);
    };

    // Show loading state while initializing core data
    if (isInitializing) {
        return (
            <Grid sx={{height:'100vh', overflowY:'auto'}}>
                <Bar />
                <Grid 
                    container 
                    sx={{ 
                        height: 'calc(100vh - 60px)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: 2
                    }}
                >
                    <CircularProgress size={40} />
                    <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                        Loading your workspace...
                    </Typography>
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid sx={{height:'100vh', overflowY:'auto'}}>
            <Bar />
            <ProjectSidebar 
                profile={profile} 
                permissions={permissions}
                designFirms={teamData.designFirms} 
                teamMembers={teamData.teamMembers} 
                initialTab={tab} 
                onTabChange={handleTabChange}
            />
            <ProjectsSnackbarComponent />
        </Grid>
    );
};

const ProjectSidebar = ({ profile, permissions, designFirms, teamMembers, initialTab, onTabChange }) => {
    const [associatedProjectsList, setAssociatedProjectsList] = useState([]);
    const [showProjectManager, setShowProjectManager] = useState(false);
    const [selectedProject, setSelectedProject] = useState<ProjectSchemeInterface>({} as ProjectSchemeInterface)
	const setShowSubscription = useMattoState((state) => state.setShowSubscription);
    const setProjectData = useProjectState((s) => s.setProjectData);
    const navigate = useNavigate();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isVerySmallScreen = useMediaQuery('(max-width: 450px)');
    
    const validTabs = ["Projects", "Boards", "Collections", "Recent"];
    const defaultTab = "Boards";
    const LAST_TAB_KEY = "projects_last_selected_tab"; // Key for localStorage
    
    // Helper function to get the last selected tab from localStorage
    const getLastSelectedTab = () => {
        try {
            const lastTab = localStorage.getItem(LAST_TAB_KEY);
            // Only return it if it exists and is a valid tab
            return lastTab && validTabs.includes(lastTab) ? lastTab : null;
        } catch (error) {
            // localStorage might not be available (e.g., in private browsing)
            console.warn("Could not access localStorage:", error);
            return null;
        }
    };

    // Helper function to save the selected tab to localStorage
    const saveLastSelectedTab = (tab: string) => {
        try {
            localStorage.setItem(LAST_TAB_KEY, tab);
        } catch (error) {
            // localStorage might not be available (e.g., in private browsing)
            console.warn("Could not save to localStorage:", error);
        }
    };
    
    // Convert initialTab to proper case for matching
    const formattedInitialTab = initialTab ? 
        initialTab.charAt(0).toUpperCase() + initialTab.slice(1).toLowerCase() : 
        null;
    
    // Priority: URL parameter > localStorage > default
    const [selectedTab, setSelectedTab] = useState(() => {
        // First priority: URL parameter
        if (validTabs.includes(formattedInitialTab)) {
            return formattedInitialTab;
        }
        
        // Second priority: Last selected tab from localStorage
        const lastTab = getLastSelectedTab();
        if (lastTab) {
            return lastTab;
        }
        
        // Final fallback: default tab
        return defaultTab;
    });

    // Function to check if a tab should be disabled
    const isDisabled = (tab: string) => {
        return (tab === "Projects" || tab === "Starred") && profile?.userRole !== UserProfileRole.PRO;
    };

    const handleDisabledTabClick = (tab: string) => {
        if (tab === "Projects") {
            setShowSubscription(['startTrial', 'Sign up to Pro to create & manage projects']); return; 
        }
    }

    const handleTabSelect = (tab) => {
        if (!isDisabled(tab)) {
            setSelectedTab(tab);
            saveLastSelectedTab(tab); // Save the selected tab to localStorage
            onTabChange(tab); // Update URL when tab changes
        } else {
            handleDisabledTabClick(tab);
        }
    };

    useEffect(() => {
        if (showProjectManager) {
            setProjectData({ selectedProject, designFirms, teamMembers, associatedProjectsList });
            navigate(`/projects/show/${selectedProject?.id}`);
        }
    }, [showProjectManager, history]);
    
    const tabs = ["Projects", "Boards", "Collections", "Recent"];

    // Map tab names to corresponding components
    const tabComponents = {
        Projects: <ProjectScheme permissions={permissions} designFirms={designFirms} teamMembers={teamMembers}  setShowProjectManager={setShowProjectManager} selectedProject={selectedProject} setSelectedProject={setSelectedProject} />,
        Boards: <BoardScheme designFirms={designFirms} teamMembers={teamMembers} />,
        Collections: <CollectionScheme permissions={permissions} designFirms={designFirms} teamMembers={teamMembers} associatedProjectsList={associatedProjectsList} />,
        Recent: <RecentScheme profile={profile} permissions={permissions} designFirms={designFirms} teamMembers={teamMembers} associatedProjectsList={associatedProjectsList} setShowProjectManager={setShowProjectManager} selectedProject={selectedProject} setSelectedProject={setSelectedProject} />,
        // Starred: <StarredScheme />,
    };

    // if (isLoading) {
    //     return (
    //         <Grid container>
    //             <Grid sx={{ width: "100%", textAlign: "center", padding: "50px" }}>
    //                 <Typography>Loading...</Typography>
    //             </Grid>
    //         </Grid>
    //     );
    // }

    return (
    <Grid container>
        <Grid sx={{ width: "100%" }}>
            <Grid
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    top: 0,
                    left: 0,
                    paddingTop: "20px", 
                    maxWidth: "640px"
                }}
            >
            {tabs.map((item, index) => {
                const disabled = isDisabled(item); 
                return (
                    <ListItem
                        key={index}
                        sx={{
                            padding: "4px 0px",
                            cursor: "pointer",
                            justifyContent: "center",
                            opacity: disabled ? 0.5 : 1, // Reduce opacity for disabled tabs
                        }}
                        onClick={() => handleTabSelect(item)} 
                        >
                        <Typography
                            sx={{
                                paddingTop: "3px",
                                fontWeight: selectedTab === item ? "bold" : "normal", // Apply fontWeight directly to the text
                            }}
                        >
                            {item}
                        </Typography>
                        {/* Conditionally render ProIconButton for Projects and Starred tabs */}
                        {(item === "Projects" || item === "Starred") && 
                            <ProIconButton 
                                id={item} 
                                size="28px" 
                                right={isVerySmallScreen ? "-16px" : isSmallScreen ? '-8px' : "6px"} // Apply right: -8px only for xs screen size
                            />
                        }
                    </ListItem>
                );
            })}
            </Grid>
        </Grid>
        <Grid
            sx={{
                padding: "10px",
                width: "100%",
            }}
        >
            {tabComponents[selectedTab]}
        </Grid>
    </Grid>
    );
}